import { formatCurrency, toUnitCharacter } from "@sa-nu/shared-utils/price";
import type { ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { z } from "zod";

import { LEGAL_DOCUMENTS } from "../../../../constants";
import {
  ReservationAgreeButton,
  type ReservationAgreeButtonMessages,
} from "../../ui/buttons/reservation-agree-button";

import type { FacilityFeeWaiver } from "./_utils/facility-fee-waiver";
import {
  facilityWaivedFeeInParentheses,
  NO_FACILITY_FEE_WAIVER,
} from "./_utils/facility-fee-waiver";
import { DescriptionHeader } from "./description-header";

export const dogFriendlyOptionsSchema = z
  .object({
    bringDog: z.boolean(),
    numberOfDogs: z.number(),
    agreeDogTerms: z.boolean(),
  })
  .refine((data) => {
    const { numberOfDogs, bringDog, agreeDogTerms } = data;
    if (bringDog) {
      return numberOfDogs > 0 && agreeDogTerms;
    }
    return true;
  });

export type DescriptionForDogFriendlyMessages = {
  heading: ReactNode;
  bringDogCheckboxLabel: string;
  feeWithDog: (formattedFee: string) => string;
  noAdditionalFee: string;
  numberOfDogsPlaceholder: string;
  dogCountOption: (count: number) => string;
  legalNoteBefore: string;
  legalLinkText: string;
  legalNoteAfter: string;
  agreeButtonLabel: string;
  unitCharacter: string;
  agreeButtonMessages?: ReservationAgreeButtonMessages;
};

const JA_MESSAGES: DescriptionForDogFriendlyMessages = {
  heading: (
    <>
      こちらのキャビンは、
      <br />
      愛犬と一緒に宿泊できます
    </>
  ),
  bringDogCheckboxLabel: "愛犬を同伴する",
  feeWithDog: (formattedFee) =>
    `（頭数に関わらず、滞在あたり ${formattedFee}の追加清掃料金が発生します）`,
  noAdditionalFee: "（追加料金はかかりません）",
  numberOfDogsPlaceholder: "頭数を選択してください",
  dogCountOption: (count) => `${count}頭`,
  legalNoteBefore: "",
  legalLinkText: "こちらの利用規則",
  legalNoteAfter: "をお読みいただき、ご同意の上でご利用ください。",
  agreeButtonLabel: "利用規則に同意します",
  unitCharacter: "円",
};

type Props = {
  additionalFee: number;
  skipAgreement?: boolean;
  feeWaiver?: FacilityFeeWaiver;
  messages?: DescriptionForDogFriendlyMessages;
};

export default function DescriptionForDogFriendlyFormPartial({
  additionalFee,
  skipAgreement = false,
  feeWaiver = NO_FACILITY_FEE_WAIVER,
  messages = JA_MESSAGES,
}: Props) {
  const { register, control } = useFormContext();

  const bringDog = useWatch({
    name: "dogFriendlyOptions.bringDog",
    control,
  });

  return (
    <div className="mt-[20px] w-full space-y-[20px]">
      <DescriptionHeader
        iconSrc="/i/icon_dogfriendly.svg"
        iconAlt="DogFriendly"
      >
        {messages.heading}
      </DescriptionHeader>
      <div className="text-xs md:text-sm">
        <label className="hover:cursor-pointer">
          <input
            type="checkbox"
            {...register("dogFriendlyOptions.bringDog")}
            className="mr-[9px] hover:cursor-pointer"
            data-testid="bring-dog-checkbox"
          />
          {messages.bringDogCheckboxLabel}
          {additionalFee > 0
            ? messages.feeWithDog(
                formatCurrency(additionalFee, {
                  unitCharacter: toUnitCharacter(messages.unitCharacter),
                }),
              )
            : feeWaiver.reason === "None"
              ? messages.noAdditionalFee
              : facilityWaivedFeeInParentheses(feeWaiver)}
        </label>
      </div>
      {bringDog && (
        <>
          <div className="border-b-oldsblack h-[56px] rounded-t border-b bg-white px-[12px] md:w-[280px]">
            <select
              className="h-full w-full"
              {...register("dogFriendlyOptions.numberOfDogs", {
                valueAsNumber: true,
              })}
              data-testid="number-of-dogs-select"
            >
              <option value="0">{messages.numberOfDogsPlaceholder}</option>
              <option value="1">{messages.dogCountOption(1)}</option>
              <option value="2">{messages.dogCountOption(2)}</option>
            </select>
          </div>
          {!skipAgreement && (
            <>
              <div className="text-xs md:text-sm">
                {messages.legalNoteBefore}
                <a
                  data-analytics-id="reservation_form_clicked_dog_terms"
                  href={LEGAL_DOCUMENTS.withDogs.url}
                  target="_blank"
                  className="font-bold underline"
                >
                  {messages.legalLinkText}
                </a>
                {messages.legalNoteAfter}
              </div>
              <div className="mt-[10px] flex justify-center py-[11px]">
                <ReservationAgreeButton
                  label={messages.agreeButtonLabel}
                  required
                  inputRHFArgs={register("dogFriendlyOptions.agreeDogTerms")}
                  testId="dog-terms-checkbox"
                  messages={messages.agreeButtonMessages}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
